// Base58 encoding/decoding for UUIDs
// Uses Bitcoin alphabet (no 0, O, I, l to avoid confusion)

const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const BASE = BigInt(58);

/**
 * Decode a base58 string back to UUID format
 */
export function decodeToUUID(encoded: string): string {
  let num = 0n;

  for (const char of encoded) {
    const index = ALPHABET.indexOf(char);
    if (index === -1) {
      throw new Error(`Invalid base58 character: ${char}`);
    }
    num = num * BASE + BigInt(index);
  }

  // Convert to hex and pad to 32 characters
  let hex = num.toString(16).padStart(32, '0');

  // Format as UUID (8-4-4-4-12)
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

/**
 * Encode a UUID to base58
 */
export function encodeFromUUID(uuid: string): string {
  const hex = uuid.replace(/-/g, '');
  let num = BigInt('0x' + hex);
  if (num === 0n) return ALPHABET[0];

  let result = '';
  while (num > 0n) {
    const remainder = num % BASE;
    result = ALPHABET[Number(remainder)] + result;
    num = num / BASE;
  }
  return result;
}

/**
 * Check if a string is a valid base58 encoded value
 */
export function isValidBase58(str: string): boolean {
  if (!str || str.length === 0) return false;
  for (const char of str) {
    if (ALPHABET.indexOf(char) === -1) {
      return false;
    }
  }
  return true;
}

/**
 * Check if a string looks like a UUID
 */
export function isUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Convert any video ID (base58 or UUID) to UUID format
 */
export function toUUID(id: string): string {
  if (isUUID(id)) {
    return id;
  }
  if (isValidBase58(id)) {
    return decodeToUUID(id);
  }
  throw new Error(`Invalid video ID format: ${id}`);
}
